import React, { useState, useEffect } from "react"
import { get, post, remove, put } from "../../utils/apiClient"
import Pagination from "../common/pagination"
import toast from "react-hot-toast"
import { SquarePen, Trash2 } from "lucide-react"
import PhonebookModal from "./phonebookModal"
import { formatFaxNumber } from "../../utils/common"
import Select from "react-select"

const types = ["PERSONAL", "PRACTICE"]

const Phonebook = ({ practices }) => {
  const [phonebooks, setPhonebooks] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  // const [sortConfig, setSortConfig] = useState({
  //   key: "firstName",
  //   direction: "asc",
  // })

  const [showPopup, setShowPopup] = useState(false)
  const [showDeletePopup, setShowDeletePopup] = useState(null)
  const [phonebookToEdit, setPhonebookToEdit] = useState(null)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchText, setSearchText] = useState("")
  const [selectedType, setSelectedType] = useState(null)
  const [selectedPractice, setSelectedPractice] = useState(null)
  const [hoveredRow, setHoveredRow] = useState(null)
  const [activeRow, setActiveRow] = useState(null)

  useEffect(() => {
    loadPhonebooks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchPhonebooks = async () => {
    try {
      const data = await get(`/phonebook/phonebooks`)

      const updatedArray = data.map((item) => ({
        ...item,
        type: item.practiceName !== null ? "PRACTICE" : "PERSONAL",
      }))

      setPhonebooks(updatedArray)
    } catch (error) {
      console.error("Failed to fetch phonebooks", error)
    }
  }

  const handleAddOrEditPhonebook = async (data) => {
    // return
    setActionLoading(true)
    try {
      if (phonebookToEdit) {
        let oldParams = {
          newFaxNumber: data.faxNumber,
          oldFaxNumber: data.oldFaxNumber,
          newPracticeName: "",
          oldPracticeName: "",
        }
        if (data.type === "PRACTICE") {
          oldParams = {
            newFaxNumber: data.faxNumber,
            oldFaxNumber: data.oldFaxNumber,
            newPracticeName: data.practiceName,
            oldPracticeName: data.oldPractice,
          }
        }

        await put("/phonebook/update", {
          firstName: data.firstName,
          lastName: data.lastName,
          emailAddress: data.emailAddress,
          company: data.company,
          ...oldParams,
        })
      } else {
        await post("/phonebook/save", {
          firstName: data.firstName,
          lastName: data.lastName,
          emailAddress: data.emailAddress,
          company: data.company,
          faxNumber: data.faxNumber,
          practiceName: data.practiceName,
        })
      }

      fetchPhonebooks()
      setShowPopup(false)
      setActionLoading(false)
      setPhonebookToEdit(null)

      toast.success(
        `Phonebook has been ${
          phonebookToEdit ? "updated" : "added"
        } successfully`,
        {
          duration: 5000,
        },
      )
    } catch (error) {
      setActionLoading(false)
      toast.error(error, {
        duration: 5000,
      })
    }
  }

  const handleDeletePhonebook = async () => {
    setActionLoading(true)
    try {
      if (!showDeletePopup) return

      await remove(`/phonebook/delete`, {
        type: showDeletePopup.type,
        practiceName:
          showDeletePopup.type === "PRACTICE"
            ? showDeletePopup.practiceName
            : "",
        faxNumber: showDeletePopup.faxNumber,
      })

      fetchPhonebooks()
      setShowDeletePopup(null)
      setActionLoading(false)

      toast.success("Phonebook has been removed successfully", {
        duration: 5000,
      })
    } catch (error) {
      setActionLoading(false)
      toast.error(error, {
        duration: 5000,
      })
    }
  }

  const loadPhonebooks = async () => {
    setLoading(true)
    await fetchPhonebooks()
    setLoading(false)
  }

  // Search Filter
  let filteredPhonebooks = phonebooks?.filter((practiceName) => {
    return Object.entries(practiceName).some(([key, value]) => {
      if (
        key === "firstName" ||
        key === "lastName" ||
        key === "faxNumber" ||
        key === "company" ||
        key === "emailAddress"
      ) {
        return value
          ?.toString()
          .toLowerCase()
          .includes(searchText.toLowerCase().trim())
      }

      return false
    })
  })

  if (selectedType) {
    filteredPhonebooks = filteredPhonebooks.filter(
      (rec) => rec.type === selectedType.value,
    )
  }

  if (selectedPractice) {
    filteredPhonebooks = filteredPhonebooks.filter(
      (rec) => rec.practiceName === selectedPractice.value,
    )
  }

  const startIdx = (currentPage - 1) * rowsPerPage
  const currentPhonebooks = filteredPhonebooks.slice(
    startIdx,
    startIdx + rowsPerPage,
  )
  const totalPages = Math.ceil(filteredPhonebooks.length / rowsPerPage)

  const typeOptions = [...new Set(types.map((r) => r))]
    .map((q) => ({
      value: q,
      label: q,
    }))
    .sort((a, b) => a.value.localeCompare(b.value))

  const practiceOptions = [
    ...new Set(
      phonebooks
        .filter(
          (r) =>
            r.practiceName !== null &&
            r.practiceName !== undefined &&
            r.practiceName !== "",
        )
        .map((r) => r.practiceName),
    ),
  ]
    .map((q) => ({
      value: q,
      label: q,
    }))
    .sort((a, b) => a.value.localeCompare(b.value))

  return (
    <div className="faxes-content">
      {loading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <>
          <div
            className="search-container"
            style={{
              width: "100%",
              alignItems: "end",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "5px",
            }}
          >
            <div
              className="setting-note"
              style={{ display: "flex", gap: "5px", fontWeight: "normal" }}
            >
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder={`Search...`}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value)
                    setCurrentPage(1)
                  }}
                  style={{ paddingRight: "22px" }}
                />
                <button
                  type="button"
                  onClick={() => { setSearchText(""); setCurrentPage(1) }}
                  className="search-clear-btn"
                  style={{ visibility: searchText ? "visible" : "hidden" }}
                >
                  ✕
                </button>
              </div>

              <Select
                className="basic-single"
                placeholder="Filter by Type"
                options={typeOptions}
                value={selectedType}
                // onChange={setSelectedType}
                onChange={(v) => {
                  setSelectedType(v)
                  setCurrentPage(1)
                }}
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: 0,
                    minWidth: 100,
                    height: 41,
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />

              <Select
                className="basic-single"
                placeholder="Filter by Practice"
                options={practiceOptions}
                value={selectedPractice}
                //onChange={setSelectedPractice}
                onChange={(v) => {
                  setSelectedPractice(v)
                  setCurrentPage(1)
                }}
                isClearable
                //isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: 0,
                    minWidth: 100,
                    height: 41,
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  marginLeft: "6px",
                }}
                onClick={() => {
                  setSearchText("")
                  setSelectedType(null)
                  setSelectedPractice(null)
                }}
                title="Reset"
              >
                {" "}
                ↺
              </div>
            </div>

            <button
              className="faxes-button"
              onClick={() => {
                // setEditingPhonebook(null)
                setShowPopup(true)
              }}
              style={{ marginBottom: "5px" }}
            >
              + Add Phonebook
            </button>
          </div>
          <div className="table-container">
            <table className="faxes-table">
              <thead>
                <tr>
                  <th className="faxes-th">
                    Fax Number
                  </th>
                  <th className="faxes-th">First Name</th>
                  <th className="faxes-th">Last Name</th>
                  <th className="faxes-th">Type</th>
                  <th className="faxes-th">Practice</th>
                  <th className="faxes-th">Email</th>
                  <th className="faxes-th">Company</th>
                  <th
                    className="faxes-th"
                    style={{ width: "100px", textAlign: "center" }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentPhonebooks.length > 0 ? (
                  currentPhonebooks?.map((phonebook) => {
                    const isHighlighted =
                      activeRow === phonebook.faxNumber ||
                      hoveredRow === phonebook.faxNumber
                    return (
                    <tr
                      key={phonebook.faxNumber}
                      className={`fax-item${isHighlighted ? " row-highlighted" : ""}`}
                      style={{ cursor: "default" }}
                      onMouseEnter={() => {
                        setHoveredRow(phonebook.faxNumber)
                        if (activeRow !== phonebook.faxNumber) setActiveRow(null)
                      }}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <td className="faxes-td">
                        {formatFaxNumber(phonebook.faxNumber)}
                      </td>
                      <td className="faxes-td">{phonebook.firstName}</td>
                      <td className="faxes-td">{phonebook.lastName}</td>
                      <td className="faxes-td">{phonebook.type}</td>
                      <td className="faxes-td">{phonebook.practiceName}</td>
                      <td className="faxes-td">{phonebook.emailAddress}</td>
                      <td className="faxes-td">{phonebook.company}</td>

                      <td className="faxes-td" style={{ textAlign: "center" }}>
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => { setPhonebookToEdit(phonebook); setActiveRow(phonebook.faxNumber) }}
                        >
                          <SquarePen size="18" color="black" />
                        </span>
                        <span
                          style={{ cursor: "pointer", marginLeft: "10px" }}
                          onClick={() => {
                            setActiveRow(phonebook.faxNumber)
                            setShowDeletePopup({
                              type: phonebook.type,
                              practiceName: phonebook.practiceName,
                              faxNumber: phonebook.faxNumber,
                            })
                          }}
                        >
                          <Trash2 size="18" color="red" />
                        </span>
                      </td>
                    </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="100%"
                      style={{
                        textAlign: "center",
                        padding: "10px",
                        border: "1px solid #ccc",
                      }}
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {currentPhonebooks.length > 0 && (
            <div
              className="faxes-pagination"
              style={{
                marginTop: "8px",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {currentPhonebooks.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  rowsPerPage={rowsPerPage}
                  totalRows={currentPhonebooks.length}
                  onPageChange={(page) => setCurrentPage(page)}
                  onRowsPerPageChange={(count) => {
                    setRowsPerPage(count)
                    setCurrentPage(1) // Reset to first page on change
                  }}
                />
              )}
            </div>
          )}
        </>
      )}

      {(showPopup || phonebookToEdit) && (
        <PhonebookModal
          onSave={handleAddOrEditPhonebook}
          actionLoading={actionLoading}
          onClose={() => {
            setShowPopup(false)
            setPhonebookToEdit(null)
          }}
          practices={practices}
          phonebookToEdit={phonebookToEdit}
        />
      )}

      {showDeletePopup && (
        <DeleteConfirmationPopup
          onConfirm={handleDeletePhonebook}
          onCancel={() => setShowDeletePopup(null)}
          actionLoading={actionLoading}
        />
      )}
    </div>
  )
}

export default Phonebook

const DeleteConfirmationPopup = ({ onConfirm, onCancel, actionLoading }) => {
  return (
    <div className="popup-overlay">
      <div className="popup" style={{ padding: "20px" }}>
        {actionLoading ? (
          <div>In progress ...</div>
        ) : (
          <div>
            <h3>Confirm Deletion</h3>
            <p style={{ marginTop: "5px" }}>
              Are you sure you want to delete this phonebook?
            </p>
            <div className="popup-buttons">
              <button className="cancel" onClick={onCancel}>
                Cancel
              </button>
              <button className="delete" onClick={onConfirm}>
                Delete
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
